import { ActionLink, Container, Stack } from '@spaceship-fspl/components';
import {
  color,
  getSpace,
  map,
  paddingBottom,
  text,
  textAlign,
} from '@spaceship-fspl/styles';
import { Tags } from '@tryghost/content-api';
import {
  BlogTitle,
  BlogVerticalMarginContainer,
  getBlogPageLayoutStaticProps,
  getMetadataPropsFromPrismicSlice,
  Metadata,
  MetadataProps,
  withBlogPageLayout,
} from 'components';
import { getTags } from 'data/ghost/api-cached';
import { getLearnCategoriesPage } from 'data/prismic';
import Link from 'next/link';
import * as React from 'react';
import styled from 'styled-components';

interface CategoriesPageProps {
  tags: Tags;
  metadata: MetadataProps;
  title: string;
}

const Columns = styled.ul`
  padding-left: 0;
  margin: 0;

  ${map({ md: 'xl', lg: 'xxl' }, (gap) => `margin-bottom: -${getSpace(gap)};`)}
  ${map({ xs: 1, md: 3, lg: 4 }, (count) => `column-count: ${count};`)}
  ${map({ md: 'xl', lg: 'xxl' }, (gap) => `column-gap: ${getSpace(gap)};`)}

  li {
    list-style: none;
    padding: 0;

    ${paddingBottom({ xs: 'md', lg: 'lg' })}
  }
`;

const StyledActionLink = styled(ActionLink)`
  ${text({ variant: 1 })}
  ${textAlign({ md: 'center', lg: 'left' })}
  ${color('neutral.100')}

  :hover {
    ${color('indigo.070')}
  }
`;

const CategoriesPage: React.FC<
  React.PropsWithChildren<CategoriesPageProps>
> = ({ tags, metadata, title }) => (
  <BlogVerticalMarginContainer>
    <Container>
      <Metadata {...metadata} />
      <Stack spaceY={{ xs: 'lg', lg: 'xl' }}>
        <BlogTitle>{title}</BlogTitle>
        <Columns>
          {tags.map(({ slug, name }) => (
            <li key={slug}>
              <Link
                href="/learn/tag/[slug]/"
                as={`/learn/tag/${slug}/`}
                passHref={true}
                legacyBehavior
              >
                <StyledActionLink color="neutral.080">{name}</StyledActionLink>
              </Link>
            </li>
          ))}
        </Columns>
      </Stack>
    </Container>
  </BlogVerticalMarginContainer>
);

export const getStaticProps = getBlogPageLayoutStaticProps<CategoriesPageProps>(
  async () => {
    const document = await getLearnCategoriesPage();
    const props: CategoriesPageProps = {
      tags: await getTags({ limit: 'all' }),
      title: document.title[0]?.text || 'Categories',
    } as CategoriesPageProps;

    if (document.body) {
      for (const slice of document.body) {
        if (slice.__typename === 'Learn_categories_pageBodyMetadata') {
          props.metadata = getMetadataPropsFromPrismicSlice(slice);
          break;
        }
      }
    }

    return { props };
  },
);

export default withBlogPageLayout()(CategoriesPage);
